
/**
 * Author: Francisco Edgardo Hernández Palacios.
 * File description: Service for user maintenance.
 */

// Essential Imports
import { ComponentFactoryResolver, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

// Rxjs
import { Observable } from 'rxjs';
import { mergeMapTo } from 'rxjs/operators';

//Firebase
import firebase from 'firebase';
import { AngularFireAuth } from '@angular/fire/auth';
import { AngularFireMessaging } from '@angular/fire/messaging';
import { AngularFirestore } from '@angular/fire/firestore';

// mModal de Ant Design
import { NzModalService } from 'ng-zorro-antd/modal';


@Injectable()
export class AuthService {

  // Almacenar el token y el uid.
  token!: string;
  uid!: any;

  constructor(
    private db: AngularFirestore,
    private auth: AngularFireAuth,
    private messaging: AngularFireMessaging,
    private http: HttpClient,
    private nzModal: NzModalService,
  ) { }

  // * ==> Alertas de NGZorro <== *//

  // Modal basic alert basic

  ngModalAlert(tipo: string, titulo: string, contenido: string, func?: Function): void {
    switch (tipo) {
      case 'success':
        this.nzModal['success']({
          nzTitle: titulo,
          nzContent: contenido,
          nzOnOk: () => func && func()
        });
        break;

      case 'error':
        this.nzModal['error']({
          nzTitle: titulo,
          nzContent: contenido,
          nzOnOk: () => func && func()
        });
        break;

      case 'info':
        this.nzModal['info']({
          nzTitle: titulo,
          nzContent: contenido,
          nzOnOk: () => func && func()
        });
        break;

      default:
        this.nzModal.error({
          nzTitle: 'Error',
          nzContent: 'Modal not found',
        });
        break;
    }
  }

  // Logueo
  doLogin(value: any) {
    return new Promise((resolve, reject) => {

      // Destructurar DATA
      const { email, pass } = value;

      this.auth.signInWithEmailAndPassword(email, pass).then((res) => {
        // Almacenar el uid en la instancia del servicio.
        this.uid = res?.user || "";
        resolve(res);
      }).catch((e) => {
        reject(e);
      });
    }
    )
  };

  // Sirve para verificar si es primera vez que se loguea
  async validarUser(email: string) {
    const superAdmin = "Super Administrador";
    const admin = "Administrador";
    const miembroADmin = 'Miembro (Admin)';
    const verify = await this.verificarCorreo(email, superAdmin, admin, miembroADmin);
    return verify;
  }

  verificarCorreo(email: string, rolSuperAdmin: string, rolAdmin: string, rolMiembroAdmin: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.db.collection('usuarios', ref => ref.where('correo', "==", email)).snapshotChanges().subscribe((dataUser:any) => {
        if (!dataUser.length) resolve({ ok: false });
        else {
          const dataUserC: any = dataUser.map((a: any) => {
            return a.payload.doc.data();
          });
          const { key: keyUser, rol: refRol, changePass } = dataUserC[0];

          // Verify admin menber
          this.db.doc(refRol).get().subscribe((a: any) => {
            if (a.data().nombre == rolSuperAdmin || a.data().nombre == rolAdmin || a.data().nombre == rolMiembroAdmin) resolve({ ok: true, changePass: true });
            // if (a.data().nombre !== rolMiembroAdmin) resolve({ ok: false });
            else {
              this.db.collection('colaboradores', ref => ref.where('usuario', '==', `usuarios/${keyUser}`)).snapshotChanges().subscribe(res => {
                // Verify colaborador data
                if (!res.length) resolve({ ok: false });
                else {
                  const dataColaboradoC: any = (res.map(a => {
                    return a.payload.doc.data();
                  }));
                  const { miembro: memberRef } = dataColaboradoC[0];
                  this.db.doc(memberRef).get().subscribe((miembro: any) => {
                    if (!miembro || !miembro.data().es_activo) resolve({ ok: false });
                    else resolve({ ok: true, changePass: changePass, keyUser: keyUser });
                  });
                }
              })
            }
          });
        }
      });
    });
  }


  // Obtener permiso o al actualizar token.
  getPermiso() {
    // Solicitar permiso.
    const sub = this.messaging.requestPermission.pipe(
      // Hacer el merge map con la actualización del token.
      mergeMapTo(this.messaging.tokenChanges)
    ).subscribe((token) => {
      this.token = token || '';

      // Almacenar el token en la base de datos.
      if (this.uid) {
        this.registerToken(this.uid, this.token);
      }
      sub.unsubscribe();
    });
  }

  // Cambio de contraseña
  passwordChange(newPass: string) {
    const user = firebase.auth().currentUser;

    return user?.updatePassword(newPass);
  }

  // Modificar el doc confirmando el cambio de pass de ese correo
  confirmPassChange(key: string, campo: string) {
    return this.db.collection('usuarios').doc(key).update({ [campo]: true });
  }

  registerToken(uid: string, token: string) {
    this.http.post(
      'https://us-central1-am-cham.cloudfunctions.net/RegistrarToken',
      { uid: uid, token: token },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }).subscribe();
  }

  deleteToken(uid: string, token: string) {
    this.http.post(
      'https://us-central1-am-cham.cloudfunctions.net/EliminarToken',
      { uid: uid, token: token },
      { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) }).subscribe();
  }

  // Cerrar sesión.
  doLogout() {
    return new Promise((resolve, reject) => {

      // Eliminar token
      this.deleteToken(this.uid, this.token);
      this.auth.signOut().then((res) => {
        // Cerrar sesión.
        resolve(res);
      }).catch((e) => {
        reject(e);
      });
    });
  }

  // Verificar si el usuario está logueado y es admin.
  isLogged() {
    return new Observable((observer) => {

      const sub = this.auth.authState.subscribe((user) => {
        if (!user) {
          // Si no está logueado, retornar falso.
          observer.next(false);
          observer.complete();
        } else {
          this.uid = user.uid;
          observer.next(true);
          observer.complete();
        }
        sub.unsubscribe();
      });
    });
  }

  userUid() {
    return this.auth.authState.subscribe((user) => {
      this.uid = user?.uid || '';
    });
  }

  // Extraer usuario logeado
  getUsuario() {
    return this.auth.authState;
  }
  // Send password reset email
  enviarCorreo(email: string) {
    return this.auth.sendPasswordResetEmail(email);
  }

}
